import { useWishlist } from '@context/wishlistContext';
import { Button } from '@ngg/components';
import { HeartIcon } from '@ngg/icons';
import type {
    WishlistItemDto,
    WishlistItemWithTagsDto,
} from '@server/gql/graphql';
import usePDPWishlist from '@server/hooks/usePDPWishlist';
import { useCallback, useEffect, useRef } from 'react';

const DEBOUNCE_DELAY = 350;

const WishlistButton = ({
    productId,
    variantId,
}: {
    productId: string;
    variantId?: string;
}) => {
    const {
        isInWishlist,
        addToWishlist,
        removeFromWishlist,
        setWishlistData,
        wishlistCookies,
        isFetching,
    } = useWishlist();
    const { data } = usePDPWishlist({
        productId,
        wishlistCookies,
    });
    const wishlistItem = isInWishlist(productId, variantId);
    const debounce = useRef<ReturnType<typeof setTimeout>>();

    // Updates wishlist state with fetched PDP wishlist data. Incase we don't have the data yet.
    useEffect(() => {
        if (data && data?.length > 0) {
            setWishlistData((prev) => {
                if (prev[0]?.__typename === 'WishlistItemWithTagsDto') {
                    const newData = [
                        ...prev,
                        ...data,
                    ] as WishlistItemWithTagsDto[];
                    return newData;
                }

                const newData = [...prev, ...data] as WishlistItemDto[];
                return newData;
            });
        }
    }, [data, setWishlistData]);

    const toggleWishlist = useCallback(async () => {
        if (!wishlistItem) await addToWishlist(productId, variantId);
        else if (wishlistItem?.id) await removeFromWishlist(wishlistItem.id);
    }, [addToWishlist, removeFromWishlist, productId, variantId, wishlistItem]);

    const handleWishlistClick = useCallback(
        async (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();

            if (isFetching) return;

            await new Promise((resolve) => {
                if (debounce.current) clearTimeout(debounce.current);
                debounce.current = setTimeout(resolve, DEBOUNCE_DELAY);
            }).then(() => toggleWishlist());
        },
        [toggleWishlist, isFetching],
    );

    return (
        <Button
            type="button"
            onClick={handleWishlistClick}
            className="group w-10 border border-black text-xl transition hover:opacity-75 lg:w-12">
            <HeartIcon filled={Boolean(wishlistItem)} />
        </Button>
    );
};

export default WishlistButton;
